import React, { useEffect, useState } from 'react';
import { ClipboardCheck, PlusCircle, CheckCircle2, XCircle, Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

const FUEL_LEVELS = ['E', '1/4', '1/2', '3/4', 'F'];

const CHECK_ITEMS = [
  { key: 'fire_extinguisher_present', label: 'Fire Extinguisher', extra: 'expiry' },
  { key: 'jack_present', label: 'Jack' },
  { key: 'wheel_spanner_present', label: 'Wheel Spanner' },
  { key: 'warning_triangle_present', label: 'Warning Triangle' },
  { key: 'first_aid_kit_present', label: 'First-Aid Kit' },
  { key: 'reflective_vest_present', label: 'Reflective Vest' },
  { key: 'tow_rope_present', label: 'Tow Rope' },
];

const SYSTEM_ITEMS = [
  { key: 'wipers_ok', label: 'Wipers' },
  { key: 'headlights_ok', label: 'Headlights' },
  { key: 'tail_lights_ok', label: 'Tail Lights' },
  { key: 'brake_lights_ok', label: 'Brake Lights' },
  { key: 'indicators_ok', label: 'Indicators' },
  { key: 'horn_ok', label: 'Horn' },
  { key: 'ac_ok', label: 'AC' },
  { key: 'seatbelts_ok', label: 'Seat Belts' },
  { key: 'brakes_ok', label: 'Brakes' },
];

const emptyForm = () => ({
  inspection_type: 'mobilization',
  km_reading: '',
  fuel_level: '1/2',
  fire_extinguisher_present: false,
  fire_extinguisher_expiry: '',
  jack_present: false,
  wheel_spanner_present: false,
  warning_triangle_present: false,
  first_aid_kit_present: false,
  reflective_vest_present: false,
  tow_rope_present: false,
  wipers_ok: true,
  headlights_ok: true,
  tail_lights_ok: true,
  brake_lights_ok: true,
  indicators_ok: true,
  horn_ok: true,
  ac_ok: true,
  seatbelts_ok: true,
  brakes_ok: true,
  body_condition: 'clean',
  interior_condition: 'clean',
  cleanliness: 'clean',
  remarks: '',
  inspector_name: '',
});

/**
 * VehicleInspectionChecklist
 * ---------------------------
 * Reusable checklist attached to a Delivery Note. Supports both
 * mobilization (before dispatch) and demobilization (on return).
 *
 * Props: dnId (required), assetId (optional), variant='card'|'button'.
 */
export const VehicleInspectionChecklist = ({ dnId, assetId, variant = 'card' }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (!dnId) return;
    setLoading(true);
    try {
      const res = await api.get(`/vehicle-inspections/by-dn/${dnId}`);
      setInspections(res.data.items || []);
    } catch {
      // silent — checklist may not exist yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnId]);

  const hasMobilization = inspections.some((i) => i.inspection_type === 'mobilization');
  const hasDemobilization = inspections.some((i) => i.inspection_type === 'demobilization');

  const openNew = (type) => {
    setForm({ ...emptyForm(), inspection_type: type });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.inspector_name.trim()) {
      toast.error('Inspector name is required');
      return;
    }
    if (form.km_reading === '' || Number(form.km_reading) < 0) {
      toast.error('Enter valid KM reading');
      return;
    }
    setSaving(true);
    try {
      await api.post('/vehicle-inspections', {
        ...form,
        dn_id: dnId,
        asset_id: assetId || null,
        km_reading: Number(form.km_reading),
      });
      toast.success(`${form.inspection_type === 'mobilization' ? 'Mobilization' : 'Demobilization'} inspection recorded`);
      setShowForm(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save inspection');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  return (
    <Card className="p-4 border-blue-200 bg-blue-50/30" data-testid="vehicle-inspection-checklist">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-blue-700" />
          <h3 className="font-semibold text-blue-900">Vehicle Inspection Checklist</h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!hasMobilization && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openNew('mobilization')}
              className="border-blue-300 text-blue-800 hover:bg-blue-100"
              data-testid="new-mobilization-button"
            >
              <PlusCircle className="h-4 w-4 mr-1" /> Mobilization
            </Button>
          )}
          {hasMobilization && !hasDemobilization && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openNew('demobilization')}
              className="border-orange-300 text-orange-800 hover:bg-orange-100"
              data-testid="new-demobilization-button"
            >
              <PlusCircle className="h-4 w-4 mr-1" /> Demobilization
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {loading && <p className="text-xs text-muted-foreground">Loading...</p>}
        {!loading && inspections.length === 0 && (
          <p className="text-xs text-muted-foreground">No inspections recorded yet.</p>
        )}
        {inspections.map((ins) => (
          <div
            key={ins.id}
            className="p-2.5 rounded border bg-white flex items-center justify-between gap-3 flex-wrap"
            data-testid={`inspection-row-${ins.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Truck className="h-4 w-4 text-slate-600 shrink-0" />
              <Badge className={ins.inspection_type === 'mobilization' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}>
                {ins.inspection_type === 'mobilization' ? 'Mobilization' : 'Demobilization'}
              </Badge>
              <span className="text-xs text-muted-foreground truncate">
                KM {Number(ins.km_reading).toLocaleString()} · Fuel {ins.fuel_level} · by {ins.inspector_name}
              </span>
            </div>
            <span className="text-xs text-muted-foreground shrink-0">
              {new Date(ins.created_at).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="inspection-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {form.inspection_type === 'mobilization' ? 'Mobilization Inspection' : 'Demobilization Inspection'}
            </DialogTitle>
            <DialogDescription>
              Complete this checklist before {form.inspection_type === 'mobilization' ? 'dispatching' : 'accepting return of'} the vehicle.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Basic */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label>KM Reading *</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.km_reading}
                  onChange={(e) => setForm({ ...form, km_reading: e.target.value })}
                  data-testid="inspect-km-input"
                />
              </div>
              <div>
                <Label>Fuel Level</Label>
                <Select value={form.fuel_level} onValueChange={(v) => setForm({ ...form, fuel_level: v })}>
                  <SelectTrigger data-testid="inspect-fuel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Inspector Name *</Label>
                <Input
                  value={form.inspector_name}
                  onChange={(e) => setForm({ ...form, inspector_name: e.target.value })}
                  placeholder="Full name"
                  data-testid="inspect-inspector-input"
                />
              </div>
            </div>

            {/* Safety / Tools */}
            <div>
              <Label className="text-sm">Safety & Tools</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {CHECK_ITEMS.map((it) => (
                  <label key={it.key} className="flex items-center gap-2 text-sm cursor-pointer bg-white p-2 rounded border">
                    <input
                      type="checkbox"
                      checked={!!form[it.key]}
                      onChange={() => toggle(it.key)}
                      data-testid={`inspect-${it.key}`}
                    />
                    <span>{it.label}</span>
                  </label>
                ))}
              </div>
              {form.fire_extinguisher_present && (
                <div className="mt-2">
                  <Label className="text-xs">Fire Extinguisher Expiry</Label>
                  <Input
                    type="date"
                    value={form.fire_extinguisher_expiry}
                    onChange={(e) => setForm({ ...form, fire_extinguisher_expiry: e.target.value })}
                    data-testid="inspect-fire-expiry-input"
                  />
                </div>
              )}
            </div>

            {/* Systems */}
            <div>
              <Label className="text-sm">Vehicle Systems (OK = working)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5">
                {SYSTEM_ITEMS.map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => toggle(it.key)}
                    className={`flex items-center justify-between gap-2 p-2 rounded border text-sm ${
                      form[it.key] ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-red-50 border-red-300 text-red-900'
                    }`}
                    data-testid={`inspect-sys-${it.key}`}
                  >
                    <span>{it.label}</span>
                    {form[it.key] ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Condition */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Body Condition</Label>
                <Select value={form.body_condition} onValueChange={(v) => setForm({ ...form, body_condition: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clean">Clean</SelectItem>
                    <SelectItem value="minor_scratches">Minor scratches</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Interior Condition</Label>
                <Select value={form.interior_condition} onValueChange={(v) => setForm({ ...form, interior_condition: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clean">Clean</SelectItem>
                    <SelectItem value="worn">Worn</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cleanliness</Label>
                <Select value={form.cleanliness} onValueChange={(v) => setForm({ ...form, cleanliness: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clean">Clean</SelectItem>
                    <SelectItem value="dirty">Dirty</SelectItem>
                    <SelectItem value="very_dirty">Very Dirty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Remarks</Label>
              <Textarea
                rows={2}
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder="Any damage, missing items, condition notes..."
                data-testid="inspect-remarks-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="inspect-save-button">
              {saving ? 'Saving...' : 'Save Inspection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default VehicleInspectionChecklist;
